/* eslint-disable formatjs/no-literal-string-in-jsx */
import Button from '@atlaskit/button/new';
import { HelperMessage } from '@atlaskit/form';
import Link from '@atlaskit/link';
import { JiraIcon } from '@atlaskit/logo';
import ModalDialog from '@atlaskit/modal-dialog';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import { Radio } from '@atlaskit/radio';
import { token } from '@atlaskit/tokens';
import { Modal } from '@atlassian/growth-pattern-library-modal';
import { css } from '@compiled/react';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { ModalOnboardingImage } from './ModalOnboardingImage';
import { messages } from './messages';
import {
	COPY_LIST_SETTINGS_NO,
	COPY_LIST_SETTINGS_MIGRATE_ALL_SPACES,
	COPY_LIST_SETTINGS_MIGRATE_CURRENT_SPACE,
} from '../placements/in-app-v2/issue-navigator-placement/constants';

const MODAL_WIDTH = 968;
const MODAL_HEIGHT = 523;
const MODAL_HEIGHT_WITH_LIST_PREFERENCE = 619;
const TELL_ME_MORE_URL =
	'https://community.atlassian.com/forums/Jira-articles/We-re-merging-List-and-All-work-one-powerful-view-for-all-teams/ba-p/3090105';

type HighImpactModalProps = {
	onClose: () => void;
	onExploreListViewButtonClick: () => void;
	hasListPreference?: boolean;
	copyListSettings: string;
	setCopyListSettings: (value: string) => void;
	projectName?: string;
};

const linkContainerStyles = css({
	marginTop: token('space.150'),
	marginBottom: token('space.negative.100'),
	display: 'flex',
	// Make the focus ring sit within the bounds of the modal content
	'a:focus, a:focus-visible': {
		outlineOffset: token('space.negative.025'),
		borderRadius: token('radius.small', '4px'),
	},
});

const ModalContentWithListPreference = ({
	copyListSettings,
	setCopyListSettings,
	projectName,
}: {
	copyListSettings: string;
	setCopyListSettings: (value: string) => void;
	projectName?: string;
}) => {
	const { formatMessage } = useIntl();
	const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		// eslint-disable-next-line @atlassian/ssr-linting/no-dom-globals-all-member-expressions
		setCopyListSettings(event.target.value);
	};
	return (
		<>
			<Text weight="bold">{formatMessage(messages.copyListSettingsDescription)}</Text>
			<Stack space="space.200">
				<Radio
					label={
						projectName
							? formatMessage(messages.copyListSettingsYesSingleSpace, {
									spaceName: <b>{projectName}</b>,
								})
							: formatMessage(messages.copyListSettingsYesSingleSpace, {
									spaceName: formatMessage(messages.spaceNameFallback),
								})
					}
					value={COPY_LIST_SETTINGS_MIGRATE_CURRENT_SPACE}
					isChecked={copyListSettings === COPY_LIST_SETTINGS_MIGRATE_CURRENT_SPACE}
					onChange={onChange}
				/>
				<Radio
					label={formatMessage(messages.copyListSettingsYesAllSpaces)}
					value={COPY_LIST_SETTINGS_MIGRATE_ALL_SPACES}
					isChecked={copyListSettings === COPY_LIST_SETTINGS_MIGRATE_ALL_SPACES}
					onChange={onChange}
				/>
				<Radio
					label={
						<>
							<Text>{formatMessage(messages.copyListSettingsNo)}</Text>
							<HelperMessage>{formatMessage(messages.copyListSettingsNoDescription)}</HelperMessage>
						</>
					}
					value={COPY_LIST_SETTINGS_NO}
					isChecked={copyListSettings === COPY_LIST_SETTINGS_NO}
					onChange={onChange}
				/>
			</Stack>
		</>
	);
};

export const HighImpactModal = ({
	onClose,
	onExploreListViewButtonClick,
	hasListPreference,
	copyListSettings,
	projectName,
	setCopyListSettings,
}: HighImpactModalProps) => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const exploreButtonRef = useRef<HTMLButtonElement | null>(null);

	return (
		<ModalDialog
			width={MODAL_WIDTH}
			height={hasListPreference ? MODAL_HEIGHT_WITH_LIST_PREFERENCE : MODAL_HEIGHT}
			onOpenComplete={() => {
				fireAnalyticsEvent({
					eventType: 'screen',
					name: 'jiraNinListMergeModalOnboardingViewed',
					attributes: {
						hasListPreference,
					},
				});
			}}
			onCloseComplete={() => {
				fireAnalyticsEvent({
					eventType: 'screen',
					name: 'jiraNinListMergeModalOnboardingClosed',
					attributes: {
						hasListPreference,
					},
				});
			}}
			testId="high-impact-modal"
			autoFocus={exploreButtonRef}
			onClose={onClose}
		>
			<Modal
				renderedInsideEntrypoint
				title={formatMessage(messages.heading)}
				subtitle={
					<>
						<Text>{formatMessage(messages.description)}</Text>
						<div css={linkContainerStyles}>
							<Link href={TELL_ME_MORE_URL} target="_blank">
								{formatMessage(messages.learnMoreLink)}
							</Link>
						</div>
					</>
				}
				sideContent={<ModalOnboardingImage copyListSettings={copyListSettings} />}
				actions={
					<Button
						appearance="primary"
						onClick={onExploreListViewButtonClick}
						ref={exploreButtonRef}
					>
						{formatMessage(
							hasListPreference
								? messages.applySettingsButtonLabel
								: messages.exploreListViewButtonLabel,
						)}
					</Button>
				}
				logo={<JiraIcon appearance="brand" shouldUseNewLogoDesign size="small" />}
			>
				{hasListPreference && (
					<ModalContentWithListPreference
						copyListSettings={copyListSettings}
						setCopyListSettings={setCopyListSettings}
						projectName={projectName}
					/>
				)}
			</Modal>
		</ModalDialog>
	);
};
